// „Abrechnung“ under a document's amount: the total stays the full invoice amount; below it what reduced it and what
// was paid — NC 92587 −120,00 · Escompte 2,0 % −21,69 · Bezahlt 1.062,91 (01.10.2026 · BIL). Data: settlement of the
// API (backend compta/documents/settlement.ts). Credit notes show what they were used for and what is still open.
import type { ReactElement } from 'react';
import { formatEur, toCents } from '../api/money';
import type { Settlement } from '../data/compta';
import { tr2 } from '../i18n/lang';
import { dmy } from '../screens/import/format';

const eur = (v: string) => formatEur(toCents(v));
const row = (label: string, value: string, color = '#9B9698', key?: string) => (
  <div key={key ?? label} style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, font: "500 11.5px/1.35 'JetBrains Mono',monospace", color, whiteSpace: 'nowrap' }}>
    <span style={{ font: '500 11.5px/1.35 Inter,sans-serif' }}>{label}</span><span>{value}</span>
  </div>
);

/**
 * Lines for an invoice/charge/payslip. Always one statement about payment for payable documents: paid (with date and
 * account), marked paid but without payment details (e.g. imported from Notion without „Payée le“), or still open.
 * `status` is the document's status; `payable` false (outgoing invoice, credit note …) shows only credits/payments.
 */
export function settlementLines(s: Settlement | null | undefined, status?: string, payable = true, hint = true): ReactElement | null {
  if (!s) return null;
  const markedPaid = status === 'PAID' || status === 'ARCHIVED';
  if (!s.paid && !s.credits.length) {
    if (!payable) return null;
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,.08)', minWidth: 170 }}>
      {markedPaid
        ? <>{row(tr2('Bezahlt', 'Payé'), tr2('ohne Zahlungsangaben', 'sans détails'), '#E0A340')}
            {hint && <div style={{ font: '400 11px/1.3 Inter,sans-serif', color: '#8C8789', textAlign: 'right', maxWidth: 220 }}>{tr2('Datum, Betrag und Konto fehlen · über „Bearbeiten“ nachtragen', 'Date, montant et compte manquent · à compléter via « Modifier »')}</div>}</>
        : row(tr2('Noch offen', 'Reste à payer'), eur(s.due), '#FBFAFA')}
    </div>;
  }
  const lines = [
    ...s.credits.map((c, i) => row('NC ' + (c.number ?? ''), '−' + eur(c.amount), '#9B9698', 'c' + i)),
    s.deduction && row(s.deduction.kind === 'escompte' ? `Escompte ${s.deduction.percent.replace('.', ',')} %` : tr2('Differenz', 'Différence'),
      '−' + eur(s.deduction.amount), s.deduction.kind === 'escompte' ? '#9B9698' : '#E0A340'),
    s.overpaid && row(tr2('mehr bezahlt', 'payé en plus'), '+' + eur(s.overpaid), '#E0A340'),
    s.paid ? row(s.open ? tr2('Teilweise bezahlt', 'Payé en partie') : tr2('Bezahlt', 'Payé'), eur(s.paid), '#6FD3CB') : row(tr2('Noch offen', 'Reste à payer'), eur(s.due), '#FBFAFA'),
    // Z.7: partial payments of a customer's facture – what is still open.
    s.open && row(tr2('Noch offen', 'Reste à payer'), eur(s.open), '#FBFAFA', 'open'),
    s.paid && s.lastPaidAt && <div key="via" style={{ font: "400 11px/1.3 'JetBrains Mono',monospace", color: '#8C8789', textAlign: 'right' }}>{dmy(s.lastPaidAt)}{s.paidVia ? ' · ' + s.paidVia : ''}</div>,
  ].filter(Boolean);
  return <div data-no-tr-amounts="1" style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,.08)', minWidth: 170 }}>{lines}</div>;
}

/** Credit note: how much of it was used for invoices and what is still open. */
export function creditLines(total: string | null | undefined, open: string | null | undefined): ReactElement | null {
  if (total == null || open == null) return null;
  const t = toCents(total), o = toCents(open), abs = (v: bigint) => (v < 0n ? -v : v), used = abs(t) - abs(o);
  if (used <= 0n) return null;
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,.08)', minWidth: 170 }}>
    {row(tr2('Verrechnet', 'Imputé'), formatEur(used))}
    {row(tr2('Noch offen', 'Reste'), formatEur(abs(o)), abs(o) > 0n ? '#FBFAFA' : '#6FD3CB')}
  </div>;
}
